import React, { lazy, Suspense, useMemo, useState } from 'react';
import { Library, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApplyPlanDialog } from 'components/IAP/ApplyPlanDialog';
import ActionsKanban from 'components/IAP/ActionsKanban';
import ActionsTable from 'components/IAP/ActionsTable';
import ActionsTimeline from 'components/IAP/ActionsTimeline';
import { INCIDENT } from 'components/IAP/ICSConstants';
import {
  openInstantiatedIncidentTask,
  useIncidentActions,
  useInstantiateIncidentTask,
} from 'components/IAP/hooks/incidentActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { ActionsViewMenu } from 'components/IAP/actionsTable/ActionsViewMenu';
import {
  ACTION_GROUPING_LABELS,
  ACTION_GROUPINGS,
  type ActionGrouping,
} from 'components/IAP/actionsTable/actionGrouping';
import {
  TIMELINE_RANGE_LABELS,
  TIMELINE_RANGES,
  type TimelineRange,
} from 'components/IAP/actionsTable/actionTimeline';
import { useActionColumnVisibility } from 'components/IAP/actionsTable/useActionColumnVisibility';
import type { ViewType } from 'components/IAP/hooks/useViewState';
import {
  phaseDefaultAssigneeId,
  phaseDefaultStatus,
  positionPhaseTasks,
  type PositionPhase,
} from './positionPhaseTasks';
import { useHwcgFlag } from 'utils/featureFlags';

/** Same reasoning as PositionTasksSection: the drawer is heavy, so pay on use. */
const ActionDrawer = lazy(() => import('components/IAP/ActionDrawer'));

type PositionPhaseTasksPanelProps = {
  phase: PositionPhase;
  /** Active plus draft occupants of the seat. */
  userIds: ReadonlySet<string>;
  positionId?: string;
  positionName?: string;
  incidentId?: string | null;
};

const EMPTY_COPY: Record<PositionPhase, string> = {
  planning: 'No planned tasks for this position.',
  execution: 'No tasks underway for this position.',
};

/**
 * One phase tab of the HWCG roster position modal: incident tasks assigned to
 * this seat or to whoever is filling it, plus a way to add another.
 *
 * A seat with nobody in it still offers Add Task, since the phase tabs are used
 * to lay out work before the roster is filled.
 */
const PositionPhaseTasksPanel: React.FC<PositionPhaseTasksPanelProps> = ({
  phase,
  userIds,
  positionId,
  positionName,
  incidentId,
}) => {
  const hwcg = useHwcgFlag();
  const { data: actions = [] } = useIncidentActions(INCIDENT, incidentId);
  const { instantiate, isPending: creatingTask } =
    useInstantiateIncidentTask(incidentId);
  const [openAction, setOpenAction] = useState<ActionEntity | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showApplyPlan, setShowApplyPlan] = useState(false);
  const [layout, setLayout] = useState<ViewType>(() =>
    phase === 'execution' ? 'timeline' : 'table'
  );
  const [grouping, setGrouping] = useState<ActionGrouping>('status');
  const [range, setRange] = useState<TimelineRange>('week');
  const { hiddenColumns, columnVisibility, onToggle, onReset } =
    useActionColumnVisibility(INCIDENT, false);
  const canApplyPlan =
    hwcg && phase === 'planning' && !!positionId && !!incidentId;

  const tasks = useMemo(
    () =>
      positionPhaseTasks(actions as ActionEntity[], userIds, phase, positionId),
    [actions, userIds, phase, positionId]
  );

  const heading = phase === 'planning' ? 'Planned Tasks' : 'Tasks Underway';

  const addTask = () => {
    if (!hwcg) {
      setShowAdd(true);
      return;
    }
    if (creatingTask) return;
    const assigneeKind = positionId ? 'position' : 'user';
    const assigneeId = positionId
      ? positionId
      : phaseDefaultAssigneeId(userIds);
    void openInstantiatedIncidentTask({
      instantiate,
      status: phaseDefaultStatus(phase),
      assigneeKind,
      assigneeId,
      onOpen: setOpenAction,
    });
  };

  return (
    <section
      className="tw:flex tw:flex-col tw:gap-3"
      aria-label={positionName ? `${heading} for ${positionName}` : heading}
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:items-baseline tw:gap-2">
          <span className="tw:font-semibold">{heading}</span>
          <span className="tw:text-sm tw:text-muted-foreground">
            {tasks.length}
          </span>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          {canApplyPlan ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-tour="hwcg-roster-apply-plan"
              onClick={() => setShowApplyPlan(true)}
            >
              <Library />
              Apply Plan
            </Button>
          ) : null}
          {layout === 'kanban' ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Label className="tw:text-xs">Group by</Label>
              <Select
                value={grouping}
                onValueChange={(next) => {
                  const value = String(next ?? '');
                  if ((ACTION_GROUPINGS as readonly string[]).includes(value)) {
                    setGrouping(value as ActionGrouping);
                  }
                }}
              >
                <SelectTrigger
                  size="sm"
                  aria-label="Group by"
                  className="tw:w-fit"
                >
                  <SelectValue>{ACTION_GROUPING_LABELS[grouping]}</SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {ACTION_GROUPINGS.map((id) => (
                    <SelectItem key={id} value={id}>
                      {ACTION_GROUPING_LABELS[id]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {layout === 'timeline' ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Label className="tw:text-xs">Window</Label>
              <Select
                value={range}
                onValueChange={(next) => {
                  const value = String(next ?? '');
                  if ((TIMELINE_RANGES as readonly string[]).includes(value)) {
                    setRange(value as TimelineRange);
                  }
                }}
              >
                <SelectTrigger
                  size="sm"
                  aria-label="Timeline window"
                  className="tw:w-fit"
                >
                  <SelectValue>{TIMELINE_RANGE_LABELS[range]}</SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {TIMELINE_RANGES.map((id) => (
                    <SelectItem key={id} value={id}>
                      {TIMELINE_RANGE_LABELS[id]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <ActionsViewMenu
            view={layout}
            onViewChange={setLayout}
            scope={INCIDENT}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggle}
            onResetColumns={onReset}
          />
          <Button
            type="button"
            size="sm"
            disabled={creatingTask}
            onClick={addTask}
          >
            <Plus />
            Add Task
          </Button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {EMPTY_COPY[phase]}
        </p>
      ) : layout === 'kanban' ? (
        <ActionsKanban
          scope={INCIDENT}
          incidentIdOverride={incidentId}
          grouping={grouping}
          filteredActions={tasks}
          onEditAction={setOpenAction}
        />
      ) : layout === 'timeline' ? (
        <ActionsTimeline
          scope={INCIDENT}
          incidentIdOverride={incidentId}
          range={range}
          filteredActions={tasks}
          onEditAction={setOpenAction}
        />
      ) : (
        <ActionsTable
          scope={INCIDENT}
          incidentIdOverride={incidentId}
          filteredActions={tasks}
          columnVisibility={columnVisibility}
          onEditAction={setOpenAction}
          onViewAction={setOpenAction}
        />
      )}

      {openAction && (
        <Suspense fallback={null}>
          <ActionDrawer
            scope={INCIDENT}
            selectedEntity={openAction}
            incidentIdOverride={incidentId}
            positionName={positionName}
            toggle={() => setOpenAction(null)}
          />
        </Suspense>
      )}
      {showApplyPlan && incidentId && positionId ? (
        <ApplyPlanDialog
          incidentId={incidentId}
          positionId={positionId}
          positionName={positionName}
          onClose={() => setShowApplyPlan(false)}
        />
      ) : null}
      {!hwcg && showAdd ? (
        <Suspense fallback={null}>
          <ActionDrawer
            scope={INCIDENT}
            incidentIdOverride={incidentId}
            positionName={positionName}
            defaultStatus={phaseDefaultStatus(phase)}
            defaultAssigneeKind="user"
            defaultAssigneeId={phaseDefaultAssigneeId(userIds)}
            toggle={() => setShowAdd(false)}
          />
        </Suspense>
      ) : null}
    </section>
  );
};

export default PositionPhaseTasksPanel;
